import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { User, UserPlus, Trash2 } from "lucide-react";
import { Screenplay, CharacterProfile } from "@/lib/user-types";

interface CharacterManagerProps {
  screenplayId: string;
  screenplay?: Screenplay;
}

export function CharacterManager({ screenplayId, screenplay }: CharacterManagerProps) {
  // State hooks
  const [newCharacterName, setNewCharacterName] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  
  // Other hooks
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  // Add character mutation
  const addCharacterMutation = useMutation({
    mutationFn: async (characterName: string) => {
      const response = await apiRequest(
        'POST', 
        `/api/screenplays/${screenplayId}/characters`, 
        { characterName }
      );
      return await response.json();
    },
    onSuccess: () => {
      setNewCharacterName('');
      setIsAdding(false);
      queryClient.invalidateQueries({ queryKey: [`/api/screenplays/${screenplayId}`] });
      toast({
        title: 'Character added',
        description: 'The character has been added to the screenplay.'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to add character',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Delete character mutation
  const deleteCharacterMutation = useMutation({
    mutationFn: async (characterName: string) => {
      const response = await apiRequest(
        'DELETE',
        `/api/screenplays/${screenplayId}/characters/${characterName}`
      );
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/screenplays/${screenplayId}`] });
      toast({
        title: 'Character removed',
        description: 'The character has been removed from the screenplay.'
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to remove character',
        description: error.message,
        variant: 'destructive'
      });
    }
  });

  // Handle form submission
  const handleAddCharacter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCharacterName.trim()) return;
    
    addCharacterMutation.mutate(newCharacterName);
  };

  // Handle character deletion
  const handleDeleteCharacter = (characterName: string) => {
    if (confirm(`Are you sure you want to remove ${characterName} from this screenplay?`)) {
      deleteCharacterMutation.mutate(characterName);
    }
  };

  // Helper for gender badges - now returns null as requested
  const getGenderBadge = (profile: any) => {
    // Removed gender badges as requested
    return null;
  };

  // Loading state
  if (!screenplay) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }
  
  // Extract character data from screenplay
  const characterDictionary = screenplay.characterDictionary || {};
  const characters = Object.entries(characterDictionary).map(([name, profile]) => ({
    name,
    profile: profile as CharacterProfile
  }));

  return (
    <div className="space-y-4">
      {/* Character list */}
      {characters.length > 0 ? (
        <div className="space-y-2">
          {characters.map(({ name, profile }) => (
            <div key={name} className="flex items-center justify-between p-2 bg-gray-50 rounded-md">
              <div className="flex items-center space-x-2">
                <User className="h-5 w-5 text-gray-500" />
                <span className="font-medium">{name}</span>
                {getGenderBadge(profile)}
              </div>
              {profile.isSpeaker === false ? (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleDeleteCharacter(name)}
                  disabled={deleteCharacterMutation.isPending}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              ) : (
                <div className="w-8 h-8"></div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500">
          No characters defined for this screenplay.
        </div>
      )}

      <Separator />

      {/* Add character form */}
      {isAdding ? (
        <form onSubmit={handleAddCharacter} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="character-name">Character Name</Label>
            <div className="flex items-center space-x-2">
              <Input
                id="character-name"
                placeholder="Enter character name"
                value={newCharacterName}
                onChange={(e) => setNewCharacterName(e.target.value)}
                className="flex-1"
                required
              />
              <Button type="submit" disabled={addCharacterMutation.isPending}>
                {addCharacterMutation.isPending ? 'Adding...' : 'Add'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsAdding(false)}>
                Cancel
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Character will be normalized to UPPERCASE and gender will be detected automatically.
            </p>
          </div>
        </form>
      ) : (
        <Button
          variant="outline"
          onClick={() => setIsAdding(true)}
          className="w-full"
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Add Character
        </Button>
      )}
    </div>
  );
}
